import {defineStore} from "pinia";
import {computed, ref} from "vue";
import {useApiClient} from "@/hooks/api-hook.ts";
import type {Change, CharterWithChanges} from "@/api/client/models/index.ts";

export const useCharterChangesStore = defineStore("charter-changes", () => {
  const api = useApiClient();

  // State
  const changes = ref<Change[]>([]);
  const chartersWithChanges = ref<CharterWithChanges[]>([]);
  const loading = ref(false);
  const error = ref<string | null>(null);

  // Getters
  const changesCount = computed(() => changes.value.length);
  const hasChanges = computed(() => changes.value.length > 0);
  const chartersWithChangesCount = computed(() => chartersWithChanges.value.length);
  const hasChartersWithChanges = computed(() => chartersWithChanges.value.length > 0);
  const isLoading = computed(() => loading.value);
  const hasError = computed(() => error.value !== null);

  // Actions
  async function fetchChanges(isActive: boolean = true): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const result = await api.changes.get({queryParameters: {isActive}});
      changes.value = result ?? [];
    } catch (err) {
      error.value = err instanceof Error ? err.message : "Failed to fetch changes";
      console.error("Error fetching changes:", err);
    } finally {
      loading.value = false;
    }
  }

  async function fetchChartersWithChanges(isArchived: boolean = false): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      const result = await api.changes.charters.get({queryParameters: {isArchived}});
      chartersWithChanges.value = result ?? [];
    } catch (err) {
      error.value = err instanceof Error ? err.message : "Failed to fetch charters with changes";
      console.error("Error fetching charters with changes:", err);
    } finally {
      loading.value = false;
    }
  }

  async function acknowledgeChanges(charterIds: number[]): Promise<void> {
    loading.value = true;
    error.value = null;

    try {
      await api.changes.acknowledge.post(charterIds);
    } catch (err) {
      error.value = err instanceof Error ? err.message : "Failed to acknowledge charter changes";
      console.error("Error acknowledging charter changes:", err);
      throw err;
    } finally {
      loading.value = false;
    }
  }

  function clearChanges() {
    changes.value = [];
    error.value = null;
  }

  function clearChartersWithChanges() {
    chartersWithChanges.value = [];
    error.value = null;
  }

  function clearError() {
    error.value = null;
  }

  return {
    // State
    changes,
    chartersWithChanges,
    loading,
    error,

    // Getters
    changesCount,
    hasChanges,
    chartersWithChangesCount,
    hasChartersWithChanges,
    isLoading,
    hasError,

    // Actions
    fetchChanges,
    fetchChartersWithChanges,
    acknowledgeChanges,
    clearChanges,
    clearChartersWithChanges,
    clearError,

    // Backward compatibility
    refresh: fetchChanges,
  };
});
